import { z } from "zod"
import { type VotingDocType, VotingDocSchema } from "@antipartido-inc/contracts-ifaces"

const VotingDocValidation = z.object({
  id: z.string({ required_error: "id is required" }),
  keyS3: z.string({ required_error: "keyS3 is required" }),
  url: z.string({ required_error: "url is required" }),
  urlCDN: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  size: z.number().optional(),
  title: z.string().optional(),
})

export class VotingDoc {
  constructor(
    private readonly _id: string,
    private readonly _keyS3: string,
    private readonly _url: string,
    private readonly _urlCDN?: string,
    private readonly _name?: string,
    private readonly _type?: string,
    private readonly _size?: number,
    private readonly _title?: string,
  ) {}

  static create({ id, keyS3, url, urlCDN, name, type, size, title }: z.infer<typeof VotingDocValidation>) {
    const { success, error } = VotingDocValidation.safeParse({ id, keyS3, url, urlCDN, name, type, size, title })
    if (!success) throw new Error(error.message)
    return new VotingDoc(id, keyS3, url, urlCDN, name, type, size, title)
  }

  static createFromAPI({ id, keyS3, url, urlCDN, name, type, size, title }: VotingDocType) {
    const { success, error } = VotingDocSchema.safeParse({ id, keyS3, url, urlCDN, name, type, size, title })
    if (!success) throw new Error(error.message)

    return VotingDoc.create({
      id,
      keyS3,
      url,
      urlCDN: urlCDN ?? undefined,
      name: name ?? undefined,
      type: type ?? undefined,
      size: size ?? undefined,
      title: title ?? undefined,
    })
  }

  get id() { return this._id } // prettier-ignore
  get keyS3() { return this._keyS3 } // prettier-ignore
  get url() { return this._url } // prettier-ignore
  get urlCDN() { return this._urlCDN } // prettier-ignore
  get name() { return this._name } // prettier-ignore
  get type() { return this._type } // prettier-ignore
  get size() { return this._size } // prettier-ignore
  get title() { return this._title } // prettier-ignore

  get isImage(): boolean {
    const type = this._type?.toLowerCase() ?? ""
    if (type.startsWith("image/")) return true
    const name = this._name?.toLowerCase() ?? ""
    return ["png", "jpg", "jpeg", "gif", "webp"].some((ext) => name.endsWith(`.${ext}`))
  }

  serialize() {
    return {
      id: this.id,
      keyS3: this.keyS3,
      url: this.url,
      urlCDN: this.urlCDN,
      name: this.name,
      type: this.type,
      size: this.size,
      title: this.title,
    }
  }
}

export type VotingDocSerialized = ReturnType<VotingDoc["serialize"]>
